import React, { useState, useEffect } from 'react';
import './EmployeeShiftView.css';
import apiService from '../services/api.service';
import employeeAuthService from '../services/employee-auth.service';

// Time conversion utilities
const minutesToTimeString = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

const weekDayNames = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

const EmployeeShiftView = () => {
  const [weeklyShifts, setWeeklyShifts] = useState({});
  const [employees, setEmployees] = useState([]);
  const [shiftTypes, setShiftTypes] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(getMonday(new Date()).toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTeamShifts, setShowTeamShifts] = useState(true);
  
  // Get Monday of current week
  function getMonday(d) {
    d = new Date(d);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is Sunday
    return new Date(d.setDate(diff));
  }
  
  // Generate dates for the current week
  const getWeekDates = () => {
    const dates = [];
    const monday = new Date(currentWeekStart);
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      dates.push({
        date: date.toISOString().split('T')[0],
        dayName: weekDayNames[i],
        displayDate: date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'numeric' })
      });
    }
    
    return dates;
  };
  
  // Get previous week
  const getPreviousWeek = () => {
    const monday = new Date(currentWeekStart);
    monday.setDate(monday.getDate() - 7);
    setCurrentWeekStart(monday.toISOString().split('T')[0]);
  };
  
  // Get next week
  const getNextWeek = () => {
    const monday = new Date(currentWeekStart);
    monday.setDate(monday.getDate() + 7);
    setCurrentWeekStart(monday.toISOString().split('T')[0]);
  };
  
  // Go to current week
  const goToCurrentWeek = () => {
    setCurrentWeekStart(getMonday(new Date()).toISOString().split('T')[0]);
  };
  
  // Fetch weekly shifts - exactly like in admin panel
  const fetchWeeklyShifts = async () => {
    setLoading(true);
    try {
      const response = await apiService.getWeeklyShifts(currentWeekStart);
      setWeeklyShifts(response.data.shifts);
      setError(null);
    } catch (err) {
      console.error('Error fetching weekly shifts:', err);
      setError('Haftalık mesailer yüklenirken bir hata oluştu: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch employees
  const fetchEmployees = async () => {
    try {
      const response = await apiService.getEmployees({ isActive: true });
      setEmployees(response.data);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };
  
  // Fetch shift types
  const fetchShiftTypes = async () => {
    try {
      const response = await apiService.getShiftTypes();
      setShiftTypes(response.data);
    } catch (err) {
      console.error('Error fetching shift types:', err);
    }
  };
  
  // Load data when component mounts or week changes
  useEffect(() => {
    fetchWeeklyShifts();
    fetchEmployees();
    fetchShiftTypes();
  }, [currentWeekStart]);
  
  // Get employee full name
  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? `${employee.name} ${employee.surname}` : 'Bilinmeyen Çalışan';
  };
  
  // Get shift type details
  const getShiftTypeDetails = (shiftTypeId) => {
    const shiftType = shiftTypes.find(type => type.id === shiftTypeId);
    return shiftType || { name: 'Bilinmeyen Vardiya', color: '#999' };
  };
  
  // Calculate total hours worked for current employee in this week
  const calculateTotalHours = () => {
    let totalMinutes = 0;
    const currentEmployeeId = employeeAuthService.getUser()?.id;
    
    if (!currentEmployeeId) return '0 saat';
    
    // Sum up all shift durations for current employee
    Object.values(weeklyShifts).forEach(dayShifts => {
      if (Array.isArray(dayShifts)) {
        dayShifts.forEach(shift => {
          if (shift.employeeId === currentEmployeeId) {
            const duration = shift.endTime - shift.startTime;
            totalMinutes += duration > 0 ? duration : 0;
          }
        });
      }
    });
    
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    return `${hours} saat ${minutes > 0 ? `${minutes} dakika` : ''}`;
  };
  
  // Generate week dates
  const weekDates = getWeekDates();

  // Get current employee ID
  const currentEmployeeId = employeeAuthService.getUser()?.id;

  // Check if a shift is for current employee
  const isMyShift = (employeeId) => {
    return employeeId === currentEmployeeId;
  };

  const getSortedVisibleShiftsForDate = (date) => {
    const dayShifts = Array.isArray(weeklyShifts?.[date]) ? weeklyShifts[date] : [];
    const visibleShifts = showTeamShifts
      ? dayShifts
      : dayShifts.filter((shift) => shift.employeeId === currentEmployeeId);

    return [...visibleShifts].sort((a, b) => {
      const aStart = Number(a?.startTime ?? 0);
      const bStart = Number(b?.startTime ?? 0);
      if (aStart !== bStart) return aStart - bStart;

      const nameA = getEmployeeName(a.employeeId);
      const nameB = getEmployeeName(b.employeeId);
      return nameA.localeCompare(nameB, 'tr', { sensitivity: 'base' });
    });
  };

  return (
    <div className="employee-shift-view">
      <div className="employee-section-header">
        <h2>Mesailerim</h2>
      </div>
      
      <div className="employee-weekly-view">
        <div className="employee-calendar-controls">
          <div className="employee-week-navigation">
            <button 
              onClick={getPreviousWeek} 
              title="Önceki Hafta"
              className="employee-nav-button"
            >
              <span className="employee-icon">«</span>
              <span className="employee-nav-text">Önceki Hafta</span>
            </button>
            <button 
              onClick={goToCurrentWeek} 
              className="employee-current-button"
            >
              Bu Hafta
            </button>
            <button 
              onClick={getNextWeek} 
              title="Sonraki Hafta"
              className="employee-nav-button"
            >
              <span className="employee-nav-text">Sonraki Hafta</span>
              <span className="employee-icon">»</span>
            </button>
          </div>
          <div className="employee-week-display">
            <h3>
              {new Date(currentWeekStart).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
              {' - '}
              {new Date(new Date(currentWeekStart).getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </h3>
          </div>
        </div>

        <div className="employee-shift-filters">
          <label className="employee-shift-filter">
            <input
              type="checkbox"
              checked={showTeamShifts}
              onChange={(e) => setShowTeamShifts(e.target.checked)}
            />
            <span>Tüm ekip vardiyalarını göster</span>
          </label>
        </div>
        
        {loading ? (
          <div className="employee-loading">Yükleniyor...</div>
        ) : error ? (
          <div className="employee-error-message">{error}</div>
        ) : (
          <>
            <div className="employee-weekly-calendar employee-weekly-calendar--grid">
              <div className="employee-calendar-header">
                {weekDates.map(day => (
                  <div key={day.date} className="employee-day-header">
                    <div className="employee-day-name">{day.dayName}</div>
                    <div className="employee-day-date">{day.displayDate}</div>
                  </div>
                ))}
              </div>
              
              <div className="employee-calendar-body">
                {weekDates.map(day => (
                  <div key={day.date} className="employee-day-column">
                    <div className="employee-day-shifts">
                      {getSortedVisibleShiftsForDate(day.date).map(shift => {
                        const shiftType = getShiftTypeDetails(shift.shiftTypeId);
                        const isCurrentEmployee = isMyShift(shift.employeeId);
                        
                        return (
                          <div 
                            key={shift.id} 
                            className={`employee-shift-card ${isCurrentEmployee ? 'employee-my-shift' : ''}`}
                            style={{ 
                              borderLeft: `4px solid ${shiftType.color}`,
                              opacity: showTeamShifts ? (isCurrentEmployee ? 1 : 0.7) : 1
                            }}
                          >
                            <div className="employee-shift-header">
                              <span className="employee-shift-type">{shiftType.name}</span>
                            </div>
                            <div className="employee-shift-employee">
                              {getEmployeeName(shift.employeeId)}
                              {isCurrentEmployee && <span className="employee-self-indicator"> (Ben)</span>}
                            </div>
                            <div className="employee-shift-time">
                              {minutesToTimeString(shift.startTime)} - {minutesToTimeString(shift.endTime)}
                            </div>
                            {shift.notes && (
                              <div className="employee-shift-notes">{shift.notes}</div>
                            )}
                          </div>
                        );
                      })}
                      
                      {getSortedVisibleShiftsForDate(day.date).length === 0 && (
                        <div className="employee-no-shifts">Vardiya yok</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="employee-weekly-list">
              {weekDates.map((day) => {
                const dayShifts = getSortedVisibleShiftsForDate(day.date);
                return (
                  <div key={day.date} className="employee-day-card">
                    <div className="employee-day-card-header">
                      <div className="employee-day-card-title">
                        <div className="employee-day-card-name">{day.dayName}</div>
                        <div className="employee-day-card-date">{day.displayDate}</div>
                      </div>
                      <div className="employee-day-card-count">{dayShifts.length}</div>
                    </div>

                    <div className="employee-day-shifts">
                      {dayShifts.map((shift) => {
                        const shiftType = getShiftTypeDetails(shift.shiftTypeId);
                        const isCurrentEmployee = isMyShift(shift.employeeId);

                        return (
                          <div
                            key={shift.id}
                            className={`employee-shift-card ${isCurrentEmployee ? 'employee-my-shift' : ''}`}
                            style={{
                              borderLeft: `4px solid ${shiftType.color}`,
                              opacity: showTeamShifts ? (isCurrentEmployee ? 1 : 0.7) : 1
                            }}
                          >
                            <div className="employee-shift-header">
                              <span className="employee-shift-type">{shiftType.name}</span>
                            </div>
                            <div className="employee-shift-employee">
                              {getEmployeeName(shift.employeeId)}
                              {isCurrentEmployee && <span className="employee-self-indicator"> (Ben)</span>}
                            </div>
                            <div className="employee-shift-time">
                              {minutesToTimeString(shift.startTime)} - {minutesToTimeString(shift.endTime)}
                            </div>
                            {shift.notes && <div className="employee-shift-notes">{shift.notes}</div>}
                          </div>
                        );
                      })}

                      {dayShifts.length === 0 && <div className="employee-no-shifts">Vardiya yok</div>}
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="employee-shift-summary">
              <div className="employee-summary-card">
                <h4>Bu Haftaki Toplam Çalışma Sürem</h4>
                <div className="employee-total-hours">{calculateTotalHours()}</div>
              </div>
              
              <div className="employee-shift-legend">
                <h4>Vardiya Türleri</h4>
                <div className="employee-legend-items">
                  {shiftTypes.map(type => (
                    <div key={type.id} className="employee-legend-item">
                      <span 
                        className="employee-legend-color" 
                        style={{ backgroundColor: type.color }}
                      ></span>
                      <span className="employee-legend-label">{type.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmployeeShiftView;
